import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCMS } from '../context/CMSContext';
import { useAuth } from '../context/AuthContext';
import { BrandLogo } from './BrandLogo';

// Logged-in nav exposes the catalogue + utilities. Logged-out nav keeps the
// landing tightly focused on conversion — no product/peptide pages visible
// until after sign-in (regulatory + ad-funnel reason).
const NAV_AUTHED = [
  { label: 'Shop', href: '/shop' },
  { label: 'Dosing', href: '/dosing' },
  { label: 'Calculator', href: '/reconstitution-calculator' },
  { label: 'Contact Us', href: '/contact' },
];
const NAV_PUBLIC = [
  { label: 'Contact Us', href: '/contact' },
];

const DesktopNav = ({ items }) => (
  <nav className="hidden md:flex items-center gap-7">
    {items.map((link) => (
      <NavLink key={link.href} to={link.href}
        className={({ isActive }) => `text-[15px] font-medium transition-colors ${isActive ? 'text-red-600' : 'text-slate-600 hover:text-[#0a0a0a]'}`}>
        {link.label}
      </NavLink>
    ))}
  </nav>
);

const AccountControls = ({ customer, onLogout }) => {
  if (customer) {
    return (
      <>
        <Link to="/account" className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 text-slate-700 text-sm font-medium">
          <User size={17} strokeWidth={1.75} />
          {customer.full_name?.split(' ')[0] || 'Account'}
        </Link>
        <button onClick={onLogout} className="hidden sm:inline-flex p-2.5 rounded-full hover:bg-slate-100 text-slate-600" aria-label="Sign out" title="Sign out">
          <LogOut size={18} strokeWidth={1.75} />
        </button>
      </>
    );
  }
  return (
    <Link to="/account" className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-semibold rounded-lg transition-colors">
      <User size={15} strokeWidth={2.2} className="sm:hidden" />
      <span className="hidden sm:inline">Sign in / Register</span>
      <span className="sm:hidden">Sign in</span>
    </Link>
  );
};

const CartButton = ({ totalItems, onClick }) => (
  <button onClick={onClick} className="relative p-2.5 rounded-full hover:bg-slate-100 transition-colors text-slate-600" aria-label="Cart">
    <ShoppingCart size={20} strokeWidth={1.75} />
    {totalItems > 0 && (
      <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{totalItems}</span>
    )}
  </button>
);

const MobileMenu = ({ open, onClose, customer, onLogout, items }) => {
  if (!open) return null;
  return (
    <div className="md:hidden border-t border-slate-200 bg-white">
      <nav className="flex flex-col px-6 py-4">
        {items.map((link) => (
          <NavLink key={link.href} to={link.href} onClick={onClose}
            className={({ isActive }) => `py-3 text-[15px] font-medium border-b border-slate-100 last:border-0 ${isActive ? 'text-red-600' : 'text-slate-700'}`}>
            {link.label}
          </NavLink>
        ))}
        {customer && (
          <button onClick={() => { onLogout(); onClose(); }} className="py-3 text-left text-[15px] font-medium text-rose-600 border-t border-slate-100">
            Sign out
          </button>
        )}
      </nav>
    </div>
  );
};

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { totalItems, setIsOpen } = useCart();
  const { t } = useCMS();
  const { customer, logoutCustomer } = useAuth();
  const navItems = customer ? NAV_AUTHED : NAV_PUBLIC;

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-4">
        <BrandLogo name={t('brand.name', 'FAT BUSTERS')} />
        <DesktopNav items={navItems} />
        <div className="flex items-center gap-1 sm:gap-2">
          <AccountControls customer={customer} onLogout={logoutCustomer} />
          {customer && <CartButton totalItems={totalItems} onClick={() => setIsOpen(true)} />}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2.5 rounded-full hover:bg-slate-100 text-slate-600" aria-label="Menu">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} customer={customer} onLogout={logoutCustomer} items={navItems} />
    </header>
  );
};

export default Header;
